import React, { useState, useEffect } from 'react'
import {
  MenuItem,
  ListItemText,
  Select,
  Checkbox,
  FormHelperText,
  ListSubheader,
  TextField,
  InputAdornment,
  Switch,
  Grid,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { FieldTitle, useInput, useTranslate } from 'react-admin'
import get from 'lodash/get'
import CircularProgress from "@mui/material/CircularProgress";

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 5.5 + ITEM_PADDING_TOP,
      width: 250,
      maxWidth: 250,
    },
  },
}

const INCLUDE_OPTION = 'include'
const EXCLUDE_OPTION = 'exclude'

export const SelectInput = ({
  source,
  label,
  options,
  defaultValue,
  validate,
  onChange,
  getOptionLabel = null,
  optionsText,
  optionValue = 'id',
  separator = ' - ',
  helperText,
  useIsFetching = false,
  isFetching = false,
  multiple = false,
  showAllOption = false,
  showSearch = false,
  hideInclude = false,
  transformDefaultValue = false,
  limitOptions = true,
  hideEmptyOption = false,
  hideHelperText = false,
  style = {},
  setSearchTextCustom = () => {},
  shouldRenderSuggestions = () => true,
  onOptionsClose = () => {},
  ...props
}) => {
  const emptyValue = multiple ? [] : null
  const translate = useTranslate()
  const [allOption, setAllOption] = useState({})
  const [searchText, setSearchText] = useState('')
  const [previousOptions, setPreviousOptions] = useState([])
  const [selectedMode, setSelectedMode] = useState(INCLUDE_OPTION)
  const { field, fieldState, isRequired } = useInput({
    source,
    validate,
    defaultValue: defaultValue || emptyValue,
  })
  const { error } = fieldState

  useEffect(() => {
    const allOptionAux = { ...allOption }
    allOptionAux[optionValue] = 'all'
    optionsText.forEach((elem) => {
      allOptionAux[elem] = 'Todos'
    })

    setAllOption(allOptionAux)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(()=>{
    if(setSearchTextCustom){
      setSearchTextCustom(searchText)
    }
  },[searchText])

  useEffect(() => {
    // Si no se pasa `defaultValue`, NO pisar el valor proveniente del record/form.
    if (defaultValue === undefined) return

    let value = defaultValue ?? emptyValue

    if (transformDefaultValue) {
      value = transformDefaultValueFn(value)
    }

    field.onChange(value)

    if (defaultValue && multiple && showAllOption) {
      setSelectedMode(
        INCLUDE_OPTION in value && value[INCLUDE_OPTION] !== null ? INCLUDE_OPTION : EXCLUDE_OPTION
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue])

  useEffect(() => {
    if (field.value) {
      if (JSON.stringify(previousOptions) !== JSON.stringify(options) && !defaultValue) {
        if (multiple) {
          let value = (showAllOption && !hideInclude ? field.value?.[selectedMode] : field.value) || []
          if (Array.isArray(value)) {
            value = value.filter((elem) => options.find((option) => option[optionValue] === elem))
          } else {
            value = [] // o manejar de otra manera si 'value' no es un array
          }

          if (showAllOption && !hideInclude && value.length > 0) {
            value = { [selectedMode]: value }
          }

          field.onChange(value)
          onChange && onChange({ target: { value: value } })
        } else if (!options.find((elem) => elem[optionValue] === field.value)) {
          field.onChange('')
          onChange && onChange({ target: { value: '' } })
        }
      }
    }

    setPreviousOptions(options)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options])

  const transformDefaultValueFn = (data) => {
    if (data) {
      let defaultValue = JSON.parse(JSON.stringify(data))
      Object.entries(defaultValue).forEach((entry) => {
        const [key, value] = entry
        if (value && Array.isArray(value)) {
          defaultValue[key] = value.map((elem) => (elem.id === 99999 ? 'all' : elem.id))
        }
      })
      return defaultValue
    }

    return null
  }

  const getText = (item) => {
    if (getOptionLabel) {
      return getOptionLabel(item)
    }

    return optionsText
      .map((option) => get(item, option))
      .filter((elem) => elem)
      .join(separator)
  }

  const getOptions = (filterOptions) => {
    if (showSearch && filterOptions) {
      if (shouldRenderSuggestions(searchText)) {
        let filteredOptions = options.filter((elem) => containsText(getText(elem), searchText))
        if (limitOptions) {
          filteredOptions = filteredOptions.slice(0, 30)
        }
        if (filteredOptions.length > 0) {
          return showAllOption && selectedMode === INCLUDE_OPTION
            ? [allOption, ...filteredOptions]
            : filteredOptions
        }
      } else {
        return showAllOption && selectedMode === INCLUDE_OPTION ? [allOption] : null
      }
    } else {
      return showAllOption && selectedMode === INCLUDE_OPTION ? [allOption, ...options] : options
    }

    return null
  }

  const containsText = (text, searchText) => {
    const textAux = text
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
    const searchTextAux = searchText
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
    return textAux.toLowerCase().indexOf(searchTextAux.toLowerCase()) > -1
  }

  const onChangeField = (e) => {
    let newValue = e.target.value

    if (multiple && showAllOption) {
      if (selectedMode === INCLUDE_OPTION) {
        let currentValue = (hideInclude ? field.value : field.value?.[selectedMode]) || []
        if (!currentValue || !Array.isArray(currentValue)) {
          currentValue = []
        }
        
        if (!currentValue.includes(allOption[optionValue]) && newValue.includes(allOption[optionValue])) {
          newValue = [allOption[optionValue]]
        } else if (
          currentValue.includes(allOption[optionValue]) &&
          !newValue.includes(allOption[optionValue])
        ) {
          newValue = { [selectedMode]: [] }
        } else {
          newValue = newValue.filter((elem) => elem !== allOption[optionValue])
        }
      }

      if (!hideInclude) {
        newValue = newValue.length > 0 ? { [selectedMode]: newValue } : { [selectedMode]: [] }
      }
    }

    field.onChange(newValue)
    onChange && onChange({ target: { value: newValue } })
  }

  const getValue = () => {
    if (multiple) {
      const values = getOptions(false)?.map((elem) => elem[optionValue]) || []
      const value = showAllOption && !hideInclude ? field.value?.[selectedMode] : field.value
      if (value && Array.isArray(value) && value.length > 0 && values && values.length > 0) {
        return (value || []).filter((elem) => values.includes(elem))
      } else {
        return []
      }
    } else {
      return getOptions(false)?.find((elem) => elem[optionValue] === field.value) ? field.value : ''
    }
  }

  const isChecked = (item) => {
    const value = showAllOption && !hideInclude ? field.value?.[selectedMode] || [] : field.value

    // Verificar si 'value' es un array y no es null
    if (Array.isArray(value) && value !== null) {
      return value.includes(item[optionValue])
    }

    return null
  }

  const isIndeterminated = (item) => {
    const value = showAllOption && !hideInclude ? field.value?.[selectedMode] || [] : field.value
    return (
      value.length > 0 &&
      item[optionValue] === allOption[optionValue] &&
      !value.includes(allOption[optionValue])
    )
  }

  const onChangeMode = (e) => {
    const checked = e.target.checked
    const mode = checked ? INCLUDE_OPTION : EXCLUDE_OPTION
    const values = field.value?.[selectedMode]?.filter((elem) => elem !== allOption[optionValue]) || []
    const newValue = values.length > 0 ? { [mode]: values } : { [mode]: [] }
    field.onChange(newValue)
    onChange && onChange({ target: { value: newValue } })
    setSelectedMode(mode)
  }

  return (
    <>
      <Grid className='select' style={style}>
        {multiple && showAllOption && !hideInclude && (
          <Grid className='select-switch'>
            <Switch size='small' checked={selectedMode === INCLUDE_OPTION} onChange={onChangeMode} />
            <FieldTitle label={translate(`ra.field.${selectedMode}`)} />
          </Grid>
        )}
        <Select
          fullWidth
          size='small'
          label={label}
          className={multiple && showAllOption && !hideInclude ? 'select-include' : ''}
          multiple={multiple}
          value={getValue()}
          onChange={onChangeField}
          renderValue={(selected) => {
            const selectedOptions =
              getOptions(false)
                ?.filter((item) =>
                  multiple ? selected.includes(item[optionValue]) : selected === item[optionValue]
                )
                .slice(0, 5) || []
            return selectedOptions.map(getText).join(', ')
          }}
          MenuProps={MenuProps}
          onClose={() => {
            setSearchText('')
            onOptionsClose && onOptionsClose(field.value)
          }}
          error={error?.message ? true : false}
          {...props}
        >
          {showSearch && (
            <ListSubheader>
              {useIsFetching && isFetching &&<CircularProgress />}
              <TextField
                size='small'
                placeholder='Buscar...'
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') {
                    e.stopPropagation()
                  }
                }}
              />
            </ListSubheader>
          )}

          {!hideEmptyOption && !isRequired && !multiple && options.length > 0 && (
            <MenuItem value={null}>
              <span>&nbsp;</span>
            </MenuItem>
          )}

          {getOptions(true)?.map((item, index) => (
            <MenuItem key={index} value={item[optionValue]}>
              {multiple && (
                <Checkbox
                  className='select-checkbox'
                  size='small'
                  checked={isChecked(item)}
                  indeterminate={isIndeterminated(item)}
                />
              )}
              <ListItemText primary={getText(item)} />
            </MenuItem>
          )) || (
              <MenuItem disabled>
                <em>{translate('ra.validation.noOptionsText')}</em>
              </MenuItem>
            )}
        </Select>
      </Grid>

      {!hideHelperText && (
        <FormHelperText className='form-helper-text' error={error?.message ? true : false}>
          {error?.message || helperText}
        </FormHelperText>
      )}
    </>
  )
}
